import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Tree, Typography } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import { Est3WbsLayerTag } from '@/components/epc/shared/Est3WbsLayerTag';
import {
  filterNodesBySearch,
  siblingsOf,
  visiblePreorder,
  type LibraryWbsNode,
  resolveTreeDrop,
  siblingOrderAfterMove,
  computeSortOrders,
} from '@/utils/wbsLibraryTreeUtils';
import styles from './EpcWbsLibrarySortableTree.module.css';

const { Text } = Typography;

export interface EpcWbsLibrarySortableTreeProps {
  nodes: LibraryWbsNode[];
  loading?: boolean;
  selectedId?: string;
  searchFilter?: string;
  readOnly?: boolean;
  templateCounts?: Record<string, number>;
  onSelect: (id: string) => void;
  onEdit?: (node: LibraryWbsNode) => void;
  onDelete?: (nodeId: string) => void;
  onShowShortcuts?: () => void;
  onMove: (updates: Array<{ id: string; parentId?: string | null; sortOrder: number }>) => Promise<void>;
}

function buildTreeNodes(
  nodes: LibraryWbsNode[],
  parentId: string | null,
  templateCounts?: Record<string, number>
): TreeDataNode[] {
  return siblingsOf(nodes, parentId).map((n) => {
    const kids = buildTreeNodes(nodes, String(n.id), templateCounts);
    const tplCount = templateCounts?.[String(n.id)];
    return {
      key: String(n.id),
      title: (
        <span className={styles.nodeTitle}>
          <Text code className={styles.nodeCode}>
            {n.code}
          </Text>
          <Text ellipsis className={styles.nodeName}>
            {n.name}
          </Text>
          <Est3WbsLayerTag layerCode={n.layer_code} showHint={false} />
          {String(n.layer_code) === 'PACKAGE' && tplCount ? (
            <span className={styles.tplBadge}>{tplCount} CBS</span>
          ) : null}
        </span>
      ),
      children: kids.length ? kids : undefined,
      isLeaf: kids.length === 0,
    };
  });
}

export const EpcWbsLibrarySortableTree: React.FC<EpcWbsLibrarySortableTreeProps> = ({
  nodes,
  loading,
  selectedId,
  searchFilter,
  readOnly,
  templateCounts,
  onSelect,
  onEdit,
  onDelete,
  onShowShortcuts,
  onMove,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [moving, setMoving] = useState(false);

  const filtered = useMemo(
    () => filterNodesBySearch(nodes, searchFilter || ''),
    [nodes, searchFilter]
  );

  const treeData = useMemo(() => buildTreeNodes(filtered, null, templateCounts), [filtered, templateCounts]);

  useEffect(() => {
    if (!expandedKeys.length && filtered.length) {
      setExpandedKeys(filtered.map((n) => String(n.id)));
    }
  }, [filtered.length]);

  useEffect(() => {
    if (searchFilter?.trim()) {
      setExpandedKeys(filtered.map((n) => String(n.id)));
    }
  }, [searchFilter, filtered]);

  const navigate = useCallback(
    (delta: number) => {
      const expanded = new Set(expandedKeys.map(String));
      const visible = visiblePreorder(filtered, expanded);
      if (!visible.length) return;
      const idx = selectedId ? visible.findIndex((n) => String(n.id) === selectedId) : -1;
      const next = visible[Math.max(0, Math.min(visible.length - 1, idx + delta))];
      if (next) onSelect(String(next.id));
    },
    [expandedKeys, filtered, selectedId, onSelect]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!el.contains(document.activeElement) && document.activeElement !== el) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigate(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigate(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        const expanded = new Set(expandedKeys.map(String));
        const visible = visiblePreorder(filtered, expanded);
        if (visible[0]) onSelect(String(visible[0].id));
      } else if (e.key === 'End') {
        e.preventDefault();
        const expanded = new Set(expandedKeys.map(String));
        const visible = visiblePreorder(filtered, expanded);
        const last = visible[visible.length - 1];
        if (last) onSelect(String(last.id));
      } else if (e.key === 'ArrowRight' && selectedId) {
        setExpandedKeys((keys) =>
          keys.includes(selectedId) ? keys : [...keys, selectedId]
        );
      } else if (e.key === 'ArrowLeft' && selectedId) {
        setExpandedKeys((keys) => keys.filter((k) => k !== selectedId));
      } else if ((e.key === 'e' || e.key === 'E') && onEdit && selectedId && !readOnly) {
        const node = filtered.find((n) => String(n.id) === selectedId);
        if (node) onEdit(node);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && onDelete && selectedId && !readOnly) {
        e.preventDefault();
        onDelete(selectedId);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, expandedKeys, filtered, selectedId, onSelect, onEdit, onDelete, readOnly]);

  const expandAll = () => setExpandedKeys(filtered.map((n) => String(n.id)));
  const collapseAll = () => setExpandedKeys([]);

  const onDrop: TreeProps['onDrop'] = async (info) => {
    if (readOnly || moving) return;
    const dragId = String(info.dragNode.key);
    const targetId = String(info.node.key);
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
    const intent = resolveTreeDrop(nodes, dragId, targetId, dropPosition);
    if (!intent) return;

    setMoving(true);
    try {
      if (intent.type === 'reparent') {
        const newSibs = siblingOrderAfterMove(
          nodes.map((n) =>
            String(n.id) === dragId ? { ...n, parent_id: intent.newParentId } : n
          ),
          dragId,
          intent.newParentId
        );
        const orders = computeSortOrders(newSibs);
        await onMove(
          orders.map((o) => ({
            id: o.id,
            parentId: o.id === dragId ? intent.newParentId : undefined,
            sortOrder: o.sortOrder,
          }))
        );
      } else {
        const parentId = intent.parentId;
        const virtualNodes = nodes.map((n) =>
          String(n.id) === dragId ? { ...n, parent_id: parentId } : n
        );
        const newSibs = siblingOrderAfterMove(virtualNodes, dragId, parentId, intent.beforeId);
        const orders = computeSortOrders(newSibs);
        await onMove(
          orders.map((o) => ({
            id: o.id,
            parentId: o.id === dragId ? parentId : undefined,
            sortOrder: o.sortOrder,
          }))
        );
      }
    } finally {
      setMoving(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={styles.treeHost}
      tabIndex={0}
      aria-label="WBS structure tree"
    >
      <div className={styles.treeToolbar}>
        <Text type="secondary" className={styles.hint}>
          ↑↓ navigate · ←→ expand · drag reorder · E edit · Del remove
          {onShowShortcuts ? (
            <>
              {' · '}
              <Button type="link" size="small" className={styles.shortcutLink} onClick={onShowShortcuts}>
                ? shortcuts
              </Button>
            </>
          ) : null}
        </Text>
        <Space size={4}>
          <Button type="link" size="small" onClick={expandAll}>
            Expand all
          </Button>
          <Button type="link" size="small" onClick={collapseAll}>
            Collapse
          </Button>
        </Space>
      </div>
      <Tree
        blockNode
        showLine
        draggable={!readOnly && !moving}
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        selectedKeys={selectedId ? [selectedId] : []}
        onSelect={(keys) => {
          const id = keys[0] ? String(keys[0]) : '';
          if (id) onSelect(id);
        }}
        onDrop={onDrop}
        className={styles.tree}
      />
      {loading ? <div className={styles.loadingOverlay}>Loading…</div> : null}
    </div>
  );
};
